import { useCallback, useEffect, useRef, useState } from 'react';
import { checkResetToken, confirmPasswordReset, requestPasswordReset, validateNewPassword } from '../models/passwordResetModel';

// "Forgot password?" form. The backend answers the same way whether or not
// the email exists, so the only states are idle, sending, sent and failed.
export function useForgotPassword() {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const busy = useRef(false);
  const submit = useCallback(async email => {
    if (busy.current) return;
    busy.current = true;
    setStatus('sending'); setMessage('');
    try { setMessage(await requestPasswordReset(email)); setStatus('sent'); }
    catch (error) { setMessage(error.message); setStatus('failed'); }
    finally { busy.current = false; }
  }, []);
  const reset = useCallback(() => { setStatus('idle'); setMessage(''); }, []);
  return { status, message, submit, reset };
}

// Reset page: checking → invalid | ready → saving → done.
export function useResetPassword(token) {
  const [stage, setStage] = useState(token ? 'checking' : 'invalid');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const busy = useRef(false);
  useEffect(() => {
    if (!token) { setStage('invalid'); return undefined; }
    let current = true;
    setStage('checking'); setError('');
    checkResetToken(token)
      .then(valid => { if (current) setStage(valid ? 'ready' : 'invalid'); })
      .catch(err => { if (current) { setError(err.message); setStage('unavailable'); } });
    return () => { current = false; };
  }, [token, attempt]);
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  const submit = useCallback(async (password, confirm) => {
    if (busy.current) return;
    const problem = validateNewPassword(password, confirm);
    if (problem) { setError(problem); return; }
    busy.current = true;
    setStage('saving'); setError('');
    try { await confirmPasswordReset(token, password); setStage('done'); }
    catch (err) {
      if (err.code === 'PASSWORD_RESET_INVALID') setStage('invalid');
      else { setError(err.message); setStage('ready'); }
    } finally { busy.current = false; }
  }, [token]);
  return { stage, error, submit, retry };
}
